import { Component } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { TranslatePipe } from 'src/app/shared/i18n/translate.pipe';
import { Tree, TreeVisibility } from 'src/app/shared/models/tree.model';
import { TreeService } from 'src/app/shared/services/tree.service';

/** Adres drzewa trafia do URL-a, więc bez znaków, które trzeba kodować. */
const URL_PATTERN = /^[a-z0-9_-]{3,50}$/;

/** Wynik okna: utworzone drzewo plus wybór, czy zacząć od importu GEDCOM. */
export interface TreeCreated extends Tree {
  importFirst?: boolean;
}

/**
 * Okno zakładania drzewa. Samodzielne, bo otwierają je dwa miejsca:
 * lista drzew w ustawieniach konta i blok startowy na stronie głównej
 * (użytkownik bez drzew). Zamyka się z utworzonym drzewem (`Tree`) albo
 * bez wartości przy anulowaniu.
 */
@Component({
    selector: 'app-tree-edit',
    templateUrl: './tree-edit.component.html',
    styleUrls: ['./tree-edit.component.css'],
    standalone: true,
    imports: [ReactiveFormsModule, MatDialogModule, MatButtonModule, MatFormFieldModule,
      MatInputModule, MatRadioModule, TranslatePipe]
})
export class TreeEditComponent {

  titleFormControl = new UntypedFormControl('', [Validators.required]);
  urlFormControl = new UntypedFormControl('', [Validators.required, Validators.pattern(URL_PATTERN)]);
  /** Nowe drzewo zaczyna jako prywatne - otwiera się je świadomie */
  visibilityFormControl = new UntypedFormControl('private');
  /** Od zera albo z pliku GEDCOM - w drugim przypadku od razu idziemy do importu. */
  startFormControl = new UntypedFormControl('scratch');

  treeForm = new UntypedFormGroup({
    title: this.titleFormControl,
    url: this.urlFormControl,
    visibility: this.visibilityFormControl,
    start: this.startFormControl
  });

  creating = false;
  /** Adres podpowiadany z nazwy - dopóki nikt go nie poprawił ręcznie. */
  private urlTouchedByUser = false;

  constructor(public dialogRef: MatDialogRef<TreeEditComponent>,
              private treeService: TreeService) {
    this.urlFormControl.valueChanges.subscribe(() => {
      if (this.urlFormControl.dirty) {
        this.urlTouchedByUser = true;
      }
    });
  }

  /** Z nazwy robimy propozycję adresu - i tak trzeba go wymyślić. */
  onTitleChange() {
    if (this.urlTouchedByUser) {
      return;
    }

    const propozycja = (this.titleFormControl.value as string ?? '')
      .toLowerCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/ł/g, 'l')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 50);

    this.urlFormControl.setValue(propozycja, { emitEvent: false });
  }

  onCreate() {
    if (this.treeForm.invalid || this.creating) {
      return;
    }

    const tree = new Tree();
    tree.title = (this.titleFormControl.value as string).trim();
    tree.url = (this.urlFormControl.value as string).trim().toLowerCase();
    tree.visibility = this.visibilityFormControl.value as TreeVisibility;

    this.creating = true;
    this.treeService.insertTree(tree).subscribe({
      next: (data) => {
        this.creating = false;
        // błąd biznesowy (zajęty adres) wraca jako sama koperta ze statusem,
        // bez pola data - komunikat pokazuje interceptor, okno zostaje otwarte
        if (data && data.id) {
          const wynik: TreeCreated = { ...data, importFirst: this.startFormControl.value === 'gedcom' };
          this.dialogRef.close(wynik);
        }
      },
      error: () => this.creating = false
    });
  }

  onCancelClick(): void {
    this.dialogRef.close();
  }
}
